import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FunnelIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  PhotoIcon,
  ArrowUpRightIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

/* ----------------------------------------------------------------------------
   GOOGLE FONTS
---------------------------------------------------------------------------- */
const FontLoader = () => (
  <link
    rel="stylesheet"
    href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800&family=Roboto:wght@300;400;500;700&display=swap"
  />
);

/* ----------------------------------------------------------------------------
   MOCK REQUESTS DATA  — You can replace with API later
---------------------------------------------------------------------------- */
const REQUESTS = [
  {
    id: 1001,
    employee: "Ravi Kumar",
    date: "2025-01-21",
    requestedIn: "09:35",
    requestedOut: "18:00",
    reason: "Missed punch-in due to system failure.",
    status: "Pending",
    attachments: [
      { id: 1, type: "image", url: "https://picsum.photos/200" },
      { id: 2, type: "pdf", url: "/sample-proof.pdf" },
    ],
  },
  {
    id: 1002,
    employee: "Sneha Singh",
    date: "2025-01-20",
    requestedIn: "10:00",
    requestedOut: "19:30",
    reason: "Medical visit, forgot to punch.",
    status: "Approved",
    attachments: [],
  },
  {
    id: 1003,
    employee: "Amit Verma",
    date: "2025-01-18",
    requestedIn: "09:15",
    requestedOut: "17:45",
    reason: "Punched out incorrectly.",
    status: "Rejected",
    attachments: [{ id: 1, type: "image", url: "https://picsum.photos/210" }],
  },
];

/* ----------------------------------------------------------------------------
   STATUS BADGE
---------------------------------------------------------------------------- */
const StatusBadge = ({ status }) => {
  const style =
    status === "Pending"
      ? "bg-yellow-100 text-yellow-700"
      : status === "Approved"
      ? "bg-green-100 text-green-700"
      : "bg-red-100 text-red-700";

  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${style}`}>
      {status}
    </span>
  );
};

/* ----------------------------------------------------------------------------
   DRAWER MODAL — FULL ENTERPRISE REVIEW PANEL
---------------------------------------------------------------------------- */
const ReviewDrawer = ({
  isOpen,
  onClose,
  request,
  handleApproval,
  handleReject,
}) => {
  const [remarks, setRemarks] = useState("");

  return (
    <AnimatePresence>
      {isOpen && request && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end"
          onClick={onClose}
        >
          <motion.div
            initial={{ x: 300 }}
            animate={{ x: 0 }}
            exit={{ x: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-white h-full shadow-xl p-6 overflow-y-auto font-roboto"
          >
            {/* HEADER */}
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold font-poppins">
                Review Correction Request
              </h2>
              <button onClick={onClose}>
                <XMarkIcon className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            {/* EMPLOYEE INFO */}
            <div className="bg-gray-50 border rounded-lg p-4 mb-6">
              <p className="text-lg font-poppins font-semibold text-gray-900">
                {request.employee}
              </p>
              <p className="text-sm text-gray-500">Request ID: {request.id}</p>
              <p className="text-sm text-gray-500 mt-2">
                Date: <span className="font-semibold">{request.date}</span>
              </p>
            </div>

            {/* REQUEST DETAILS */}
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-gray-500 font-medium">Requested Punch-In</p>
                <p className="text-gray-800 text-lg font-poppins">
                  {request.requestedIn}
                </p>
              </div>
              <div>
                <p className="text-gray-500 font-medium">Requested Punch-Out</p>
                <p className="text-gray-800 text-lg font-poppins">
                  {request.requestedOut}
                </p>
              </div>

              <div>
                <p className="text-gray-500 font-medium">Reason</p>
                <p className="text-gray-800">{request.reason}</p>
              </div>

              {/* ATTACHMENTS */}
              {request.attachments.length > 0 && (
                <div>
                  <p className="font-medium text-gray-500 mb-1">Attachments</p>
                  <div className="flex gap-3 flex-wrap">
                    {request.attachments.map((a) => (
                      <div
                        key={a.id}
                        className="w-20 h-20 border rounded-lg overflow-hidden bg-gray-100 flex flex-col justify-center items-center cursor-pointer hover:shadow"
                      >
                        {a.type === "image" ? (
                          <img
                            src={a.url}
                            alt="attach"
                            className="object-cover w-full h-full"
                          />
                        ) : (
                          <div className="flex flex-col items-center p-3">
                            <DocumentTextIcon className="h-6 w-6 text-gray-600" />
                            <p className="text-xs text-gray-600 mt-1">PDF</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* REMARKS INPUT */}
            <div className="mt-6">
              <label className="text-sm text-gray-600 font-medium">
                Manager Remarks (Optional)
              </label>
              <textarea
                className="w-full mt-2 border rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500"
                rows="3"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add remarks..."
              />
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => handleReject(request.id, remarks)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-semibold"
              >
                Reject
              </button>
              <button
                onClick={() => handleApproval(request.id, remarks)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold"
              >
                Approve
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ----------------------------------------------------------------------------
   MAIN PAGE
---------------------------------------------------------------------------- */
const ManagerAttendanceCorrectionApproval = () => {
  const [requests, setRequests] = useState(REQUESTS);
  const [filter, setFilter] = useState("Pending");
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filtered = useMemo(() => {
    return requests.filter(
      (r) =>
        (filter === "All" || r.status === filter) &&
        r.employee.toLowerCase().includes(search.toLowerCase())
    );
  }, [requests, filter, search]);

  const handleOpenDrawer = (req) => {
    setSelectedRequest(req);
    setDrawerOpen(true);
  };

  const handleApprove = (id, remarks) => {
    setRequests((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, status: "Approved", managerRemarks: remarks }
          : r
      )
    );
    setDrawerOpen(false);
  };

  const handleReject = (id, remarks) => {
    setRequests((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, status: "Rejected", managerRemarks: remarks }
          : r
      )
    );
    setDrawerOpen(false);
  };

  return (
    <div className="p-8 font-roboto bg-gray-50 min-h-screen">
      <FontLoader />

      {/* HEADER */}
      <div className="mb-8 flex justify-between items-center">
        <h1 className="text-3xl font-poppins font-bold text-gray-800">
          Attendance Correction Approvals
        </h1>
      </div>

      {/* FILTERS */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-3">
          {["Pending", "Approved", "Rejected", "All"].map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${
                filter === tab
                  ? "bg-indigo-600 text-white"
                  : "bg-white border border-gray-300"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* SEARCH */}
        <div className="flex items-center border px-3 py-2 rounded-lg bg-white shadow-sm">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search employee..."
            className="ml-2 outline-none text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white shadow rounded-xl border p-5">
        <table className="w-full text-sm">
          <thead className="text-gray-500">
            <tr>
              <th className="text-left py-2">Employee</th>
              <th className="py-2">Date</th>
              <th className="py-2">Requested In</th>
              <th className="py-2">Requested Out</th>
              <th className="py-2">Status</th>
              <th className="py-2 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t hover:bg-gray-50">
                <td className="py-3">{r.employee}</td>
                <td className="text-center">{r.date}</td>
                <td className="text-center">{r.requestedIn}</td>
                <td className="text-center">{r.requestedOut}</td>
                <td className="text-center">
                  <StatusBadge status={r.status} />
                </td>
                <td className="text-center">
                  <button
                    onClick={() => handleOpenDrawer(r)}
                    className="text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Review →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <p className="text-center py-8 text-gray-500">
            No requests found for this filter.
          </p>
        )}
      </div>

      {/* DRAWER */}
      <ReviewDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        request={selectedRequest}
        handleApproval={handleApprove}
        handleReject={handleReject}
      />
    </div>
  );
};

export default ManagerAttendanceCorrectionApproval;
